import { useRef, useState, useCallback, useEffect } from "react";
import Heading from "components/Heading/Heading";
import { useTranslation } from "react-i18next";
import { useGetHolidayPackageHighlightsQuery } from "application/features/holidayPackage/holidayPackageApiSlice";
import HolidayPackageCard from "components/HolidayPackageCard/HolidayPackageCard";
import Skeleton from "react-loading-skeleton";

const SCROLL_THRESHOLD = 4;

const SectionFeaturedHolidayPackages = () => {
  const { t, ready } = useTranslation("translation");
  const { data: highlightsData, isLoading: loadingHighlights } = useGetHolidayPackageHighlightsQuery({});

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollButtons();
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(updateScrollButtons);
    observer.observe(el);
    return () => observer.disconnect();
  }, [highlightsData, updateScrollButtons]);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.firstElementChild?.getBoundingClientRect().width ?? 300;
    const gap = 24; // gap-6 = 1.5rem = 24px
    el.scrollBy({ left: direction === "left" ? -(cardWidth + gap) : cardWidth + gap, behavior: "smooth" });
  };

  if (!ready) return null;

  const packages = highlightsData?.packages?.filter((pkg: any) => !pkg.in_review) ?? [];
  const needsScroll = packages.length > SCROLL_THRESHOLD;

  const renderCardSkeleton = () => (
    <div className="w-full">
      <Skeleton height={250} className="rounded-t-2xl" />
      <div className="p-4 space-y-4">
        <div className="space-y-2">
          <Skeleton width={120} height={16} />
          <Skeleton height={24} />
          <Skeleton height={16} />
        </div>
        <div className="flex justify-between items-center">
          <Skeleton width={100} height={20} />
          <Skeleton width={80} height={16} />
        </div>
      </div>
    </div>
  );

  const renderArrowButton = (direction: "left" | "right") => {
    const visible = direction === "left" ? canScrollLeft : canScrollRight;
    if (!visible) return null;

    return (
      <button
        className={`absolute top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white dark:bg-neutral-900 shadow-lg border border-neutral-200 dark:border-neutral-700 flex items-center justify-center hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors ${
          direction === "left" ? "left-0 -translate-x-1/2" : "right-0 translate-x-1/2"
        }`}
        onClick={() => scroll(direction)}
        aria-label={direction === "left" ? "Scroll left" : "Scroll right"}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-neutral-700 dark:text-neutral-300"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          {direction === "left" ? (
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          )}
        </svg>
      </button>
    );
  };

  return (
    <div className="relative py-16 w-full">
      <div className="w-full">
        <Heading
          isCenter
          desc={t("translation:home.featuredPackages.description")}
        >
          {t("translation:home.featuredPackages.title")}
        </Heading>

        {loadingHighlights ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-12">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="relative bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl overflow-hidden">
                {renderCardSkeleton()}
              </div>
            ))}
          </div>
        ) : needsScroll ? (
          <div className="relative mt-12">
            {renderArrowButton("left")}
            <div
              ref={scrollRef}
              onScroll={updateScrollButtons}
              className="flex gap-6 overflow-x-auto hiddenScrollbar scroll-smooth snap-x snap-mandatory pb-2"
            >
              {packages.map((pkg: any) => (
                <div
                  key={pkg.package_uuid}
                  className="flex-none w-[calc(100%-1.5rem)] sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)] xl:w-[calc(25%-1.125rem)] snap-start"
                >
                  <HolidayPackageCard data={pkg} size="default" />
                </div>
              ))}
            </div>
            {renderArrowButton("right")}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-12">
            {packages.map((pkg: any) => (
              <HolidayPackageCard
                key={pkg.package_uuid}
                data={pkg}
                size="default"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionFeaturedHolidayPackages;